import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const CTABannerSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  // Layout: prefer explicit layout, otherwise derive from centerAlign flag
  const derivedLayout = settings.centerAlign === false ? "left_aligned" : "centered"
  const layout = settings.layout || derivedLayout
  const showCTA = settings.showCTA !== false
  const buttonText = settings.buttonText || settings.ctaText || "Learn more"
  const buttonAction = settings.buttonAction || settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""
  const showBackgroundImage = settings.showBackgroundImage !== false
  const backgroundImage = settings.backgroundImageUrl || settings.backgroundImage || section.imageUrl
  const backgroundColor = settings.backgroundColor || theme?.colors?.primary || "#BD654F"
  const titleColor = settings.titleColor || theme?.colors?.textLight || "#FFFFFF"
  const subheadlineColor = settings.subheadlineColor || theme?.colors?.textLight || "#FFFFFF"
  const descriptionColor = settings.descriptionColor || theme?.colors?.textLight || "#FFFFFF"

  const handleCTAClick = () => {
    switch (buttonAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (settings.buttonUrl) {
          const url = settings.buttonUrl
          if (url) window.open(url, "_blank")
        }
        break
      case "phone":
        if (settings.phoneNumber) {
          window.location.href = `tel:${settings.phoneNumber}`
        }
        break
      case "email":
        if (settings.email) {
          window.location.href = `mailto:${settings.email}`
        }
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-6">
      {/* Text Content */}
      <div className="flex flex-col gap-2 text-center">
        {section.headline && (
          <h2
            className="text-[24px] font-bold leading-[28px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: titleColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className="text-[16px] leading-[22px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: subheadlineColor
            }}>
            {section.subheadline}
          </p>
        )}
        {section.body && (
          <p
            className="text-[16px] leading-[22px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: descriptionColor
            }}>
            {section.body}
          </p>
        )}
      </div>

      {/* CTA Button */}
      {showCTA && (
        <button
          onClick={handleCTAClick}
          className={twMerge(
            "inline-flex items-center justify-center px-8 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg",
            getButtonClasses(theme?.buttonStyle)
          )}
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {buttonText}
        </button>
      )}
    </div>
  )

  // Layout-specific rendering based on Figma design
  switch (layout) {
    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <AnimatedSection className="relative z-10 mx-auto px-4 text-center">{renderContent()}</AnimatedSection>
        </section>
      )

    case "left_aligned":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-start py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <AnimatedSection className="relative z-10 mx-auto px-4 text-left">{renderContent()}</AnimatedSection>
        </section>
      )

    case "split":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-between py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <AnimatedSection className="relative z-10 mx-auto max-w-6xl px-4">
            <div className="flex flex-col items-center justify-between gap-4">
              <div className="flex-1 text-center">{renderContent()}</div>
              {showCTA && (
                <div className="shrink-0">
                  <button
                    onClick={handleCTAClick}
                    className={twMerge(
                      "inline-flex items-center justify-center px-8 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg",
                      getButtonClasses(theme?.buttonStyle)
                    )}
                    style={applyButtonTheme(
                      {
                        backgroundColor: theme?.colors?.primary || "#BD654F",
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                      },
                      theme
                    )}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    }}>
                    {buttonText}
                  </button>
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )

    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <AnimatedSection className="relative z-10 mx-auto px-4 text-center">{renderContent()}</AnimatedSection>
        </section>
      )
  }
}

export default CTABannerSectionMobile
